import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { values } from "ramda";
import React from "react";
import DailyWebhook from "src/components/DailyWebhook.tsx";
import { useOnOffSwitch } from "src/helpers/hooks.tsx";
import { useErrorHandler } from "src/hooks/errorHandler.ts";
import { Spacing } from "../types/enum";
import {
  DailyEventTypes,
  DailyWebHookType,
  GetDailyHooksDocument,
  useCreateNewWebhookMutation,
  useGetDailyHooksQuery,
} from "../types/graphql";

/**
 * Constants
 */
const OPTIONS = values(DailyEventTypes).map((eventType) => ({
  label: eventType,
  value: eventType,
}));

const DailyHooksPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [isOpen, open, close] = useOnOffSwitch();
  const { handleError } = useErrorHandler();
  const [eventTypes, setEventTypes] = React.useState<DailyEventTypes[]>([]);
  const { data, loading, error } = useGetDailyHooksQuery({
    fetchPolicy: "cache-and-network",
  });

  const [createWebhook, { loading: createLoading }] = useCreateNewWebhookMutation({
    refetchQueries: [{ query: GetDailyHooksDocument }],
  });

  if (error) {
    enqueueSnackbar(error.message, { variant: "error" });
  }

  const onCreate = async () => {
    await handleError(createWebhook, {
      onSuccess: () => {
        enqueueSnackbar("Webhook successfully created", { variant: "success" });
        onClose();
      },
      finally: () => {
        onClose();
      },
    })({
      variables: {
        eventTypes,
      },
    });
  };

  const onClose = () => {
    close();
    setEventTypes([]);
  };

  return (
    <>
      <Box mb={Spacing.m} display="flex" alignItems="center" justifyContent="flex-end" width="100%">
        <Button variant="contained" color="secondary" onClick={open}>
          Create New
        </Button>
      </Box>

      <Card>
        {loading ? (
          <Box display="flex" justifyContent="center" p={Spacing.xxl}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="left">
                  <Typography variant="body2">URL</Typography>
                </TableCell>
                <TableCell align="left">
                  <Typography variant="body2">State</Typography>
                </TableCell>
                <TableCell align="left">Retry Type</TableCell>
                <TableCell align="left">Event Types</TableCell>
                <TableCell align="center">Created At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.getDailyHooks?.map((webhook: DailyWebHookType) => (
                <DailyWebhook webhook={webhook} key={webhook.uuid} />
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={isOpen} onClose={close} fullWidth>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center">
            <Typography variant="body1" color="textPrimary" my={Spacing.l}>
              Choose the event types for the new webhook
            </Typography>
            <FormControl fullWidth>
              <Select
                fullWidth
                multiple
                variant="outlined"
                value={eventTypes}
                onChange={({ target }: React.ChangeEvent<any>) => {
                  setEventTypes(target.value);
                }}
              >
                {OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox checked={eventTypes.indexOf(option.value) > -1} />
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions>
          <Box width="100%" display="flex" justifyContent="space-around" alignItems="center">
            <Button onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={!eventTypes.length || createLoading} onClick={onCreate}>
              Create
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DailyHooksPage;
